/**
 * Pivot between the flat `table_cell` nodes and the row/column grid the annotator edits,
 * and back. Row 0 is the column-header band, col 0 the row-header band — matching what
 * `pid_graph_agent_comp._reconstruct_table_chunks` already assumes.
 *
 * Every cell needs geometry: `parseGraph` drops a node with no `geometry_wkt`, so a
 * box-less cell would vanish on reload.
 *
 * Pure — plain nodes in, plain nodes out.
 */
import { v4 as uuidv4 } from 'uuid'
import type { TableCellNode } from '@/service/graph'

/** Axis-aligned table region in the same absolute pixel space as node x/y/width/height. */
export interface TableRegion {
  x: number
  y: number
  width: number
  height: number
}

/** Structural, not tied to `TableCellNode`, so the GT exporter's own node shape fits too. */
export interface TableCellLike {
  id: string
  type: string
  row?: number
  col?: number
  table_index?: number
  content?: string
  name?: string
  col_span?: number
  x?: number
  y?: number
  width?: number
  height?: number
  pnid_level_iii?: string
}

/** One table, pivoted out of the flat cell nodes. */
export interface TableModel<C extends TableCellLike = TableCellNode> {
  tableIndex: number
  /** Row count = max row + 1, so the grid always includes the header band. */
  nRows: number
  nCols: number
  /** `${row}:${col}` → the cell occupying it. Sparse — not every coordinate is filled. */
  cellByKey: Map<string, C>
  /** Union of the member cells' boxes — the table's footprint on the drawing. */
  region: TableRegion | null
}

/** The Level-III leaves a header band can carry (PIC001, added in #27513). */
export const HEADER_TYPE_STREAM_NUMBER = 'Stream Number'
export const HEADER_TYPE_STREAM_NAME = 'Stream Name'
export const HEADER_TYPE_PROPERTY_NAME = 'Property Name'

/** Level I/II every table cell sits under — a cell is text, never equipment. */
export const TABLE_CELL_LEVEL_I = 'Annotation'
export const TABLE_CELL_LEVEL_II = 'Text'

/**
 * Level-III leaf per header row, top-down; rows past the end are body. A list because real
 * tables stack header rows (stream names over stream numbers).
 *
 * Defaults to ONE row on purpose: how many a table stacks is a property of the drawing, so
 * baking in a customer's layout would earn a `docs/tenant-assumptions.md` row.
 */
export interface HeaderTypes {
  /** Level-III per header row, top-down. Length = how many rows are header. */
  rowTypes: string[]
  /** Level-III for col 0 (below the header rows). */
  leftCol: string
}

export const DEFAULT_HEADER_TYPES: HeaderTypes = {
  rowTypes: [HEADER_TYPE_STREAM_NUMBER],
  leftCol: HEADER_TYPE_PROPERTY_NAME,
}

/** Upper bound on stacked header rows — keeps the panel's control finite. */
export const MAX_HEADER_ROWS = 4

export const cellKey = (row: number, col: number): string => `${row}:${col}`

/** True when `col` in `row` is swallowed by a merged cell starting further left, so it holds
 *  no cell of its own and must not be rendered or filled. */
export function coveredBySpan<C extends TableCellLike>(
  model: TableModel<C>,
  row: number,
  col: number,
): boolean {
  for (let c = col - 1; c >= 0; c--) {
    const cell = model.cellByKey.get(cellKey(row, c))
    if (!cell) continue
    return c + (cell.col_span ?? 1) > col
  }
  return false
}

/** Coerce a stored coordinate to an integer index. Returns null for absent/garbage —
 *  the caller SKIPS such a cell rather than defaulting it to 0, which would silently
 *  stack unrelated cells onto the header band. */
const toIndex = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return null
  const i = Math.trunc(n)
  return i >= 0 ? i : null
}

/** A cell's displayed text. `content` is what the parser writes and what the grid edits;
 *  `name` is its mirror and only a fallback for older rows. */
export const cellText = (cell: TableCellLike): string => cell.content ?? cell.name ?? ''

/** A node whose optional geometry is known-present, so callers can read it unguarded. */
type Boxed = { x: number; y: number; width: number; height: number }

const hasBox = <T extends Partial<Boxed>>(n: T): n is T & Boxed =>
  Number.isFinite(n.x) && Number.isFinite(n.y) &&
  Number.isFinite(n.width) && Number.isFinite(n.height) &&
  (n.width ?? 0) > 0 && (n.height ?? 0) > 0

/** Bounding box enclosing every cell that has one; null when none do. */
const unionRegion = (cells: Iterable<TableCellLike>): TableRegion | null => {
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity
  for (const c of cells) {
    if (!hasBox(c)) continue
    x1 = Math.min(x1, c.x)
    y1 = Math.min(y1, c.y)
    x2 = Math.max(x2, c.x + c.width)
    y2 = Math.max(y2, c.y + c.height)
  }
  if (!Number.isFinite(x1) || x2 <= x1 || y2 <= y1) return null
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 }
}

/**
 * Pivot the graph's `table_cell` nodes into one `TableModel` per `table_index`,
 * ordered by index. Cells with unusable coordinates are dropped (they'd otherwise
 * corrupt the grid); a coordinate collision keeps the last cell seen.
 */
export function groupCellsIntoTables<C extends TableCellLike>(nodes: Iterable<C>): TableModel<C>[] {
  const byTable = new Map<number, Map<string, C>>()
  const extent = new Map<number, { maxRow: number; maxCol: number }>()

  for (const node of nodes) {
    if (node.type !== 'table_cell') continue
    const tableIndex = toIndex(node.table_index)
    const row = toIndex(node.row)
    const col = toIndex(node.col)
    if (tableIndex === null || row === null || col === null) continue

    let cells = byTable.get(tableIndex)
    if (!cells) { cells = new Map(); byTable.set(tableIndex, cells) }
    cells.set(cellKey(row, col), node)

    // A merged cell reaches past its anchor column, so the extent has to follow its span
    // — otherwise the rightmost header undersizes nCols and the last columns disappear.
    const lastCol = col + Math.max(1, toIndex(node.col_span) ?? 1) - 1
    const e = extent.get(tableIndex)
    if (e) { e.maxRow = Math.max(e.maxRow, row); e.maxCol = Math.max(e.maxCol, lastCol) }
    else extent.set(tableIndex, { maxRow: row, maxCol: lastCol })
  }

  return Array.from(byTable.keys())
    .sort((a, b) => a - b)
    .map((tableIndex) => {
      const cellByKey = byTable.get(tableIndex)!
      const { maxRow, maxCol } = extent.get(tableIndex)!
      return {
        tableIndex,
        nRows: maxRow + 1,
        nCols: maxCol + 1,
        cellByKey,
        region: unionRegion(cellByKey.values()),
      }
    })
}

/** The next free `table_index`, so a new table never lands on an existing one. */
export function nextTableIndex(models: readonly { tableIndex: number }[]): number {
  return models.reduce((max, m) => Math.max(max, m.tableIndex), -1) + 1
}

/**
 * The box for cell (row, col) in a `nRows × nCols` uniform subdivision of `region`.
 * An even split is an approximation of a real table's varying column widths — good
 * enough to place the cell on the drawing and to give the export a position, and far
 * better than the alternative (no geometry, so the cell is dropped on reload).
 */
export function interpolateCellBBox(
  region: TableRegion,
  row: number,
  col: number,
  nRows: number,
  nCols: number,
): TableRegion {
  const rows = Math.max(1, nRows)
  const cols = Math.max(1, nCols)
  const cellW = region.width / cols
  const cellH = region.height / rows
  return {
    x: region.x + col * cellW,
    y: region.y + row * cellH,
    width: cellW,
    height: cellH,
  }
}

/**
 * Header bands get their leaf, body cells none. Positional, never content-matched: a regex
 * over cell text would encode one tenant's conventions; "row 0 is header" holds everywhere.
 */
export function classificationForCell(
  row: number,
  col: number,
  headerTypes: HeaderTypes = DEFAULT_HEADER_TYPES,
): { pnid_level_i: string; pnid_level_ii: string; pnid_level_iii: string } | undefined {
  const headerRows = headerTypes.rowTypes.length
  // The corner block holds the table's own captions ("DESCRIPTION", "STREAM NO.") — they
  // name the axes, so they stay unclassified rather than posing as a stream or property.
  if (row < headerRows && col === 0) return undefined
  const levelIII = row < headerRows
    ? headerTypes.rowTypes[row]
    : col === 0 ? headerTypes.leftCol : undefined
  if (!levelIII) return undefined
  return {
    pnid_level_i: TABLE_CELL_LEVEL_I,
    pnid_level_ii: TABLE_CELL_LEVEL_II,
    pnid_level_iii: levelIII,
  }
}

/** One cell at an explicit box. Mirrors what the parser stamps, so hand-made and parsed
 *  cells are indistinguishable downstream. */
export function blankCellAt(
  tableIndex: number,
  row: number,
  col: number,
  box: TableRegion,
  headerTypes?: HeaderTypes,
  text = '',
  colSpan = 1,
): TableCellNode {
  return {
    ...(colSpan > 1 ? { col_span: colSpan } : {}),
    id: uuidv4(),
    type: 'table_cell',
    name: text,
    content: text,
    category: 'table',
    row,
    col,
    table_index: tableIndex,
    source: 'manual',
    ...box,
    ...classificationForCell(row, col, headerTypes),
  }
}

/** A blank `nRows × nCols` table tiled across `region`. The only path that interpolates a
 *  whole table — from scratch there is no observed geometry to keep. */
export function buildBlankTable(
  tableIndex: number,
  region: TableRegion,
  nRows: number,
  nCols: number,
  headerTypes?: HeaderTypes,
): TableCellNode[] {
  const cells: TableCellNode[] = []
  for (let row = 0; row < nRows; row++) {
    for (let col = 0; col < nCols; col++) {
      cells.push(blankCellAt(
        tableIndex, row, col,
        interpolateCellBBox(region, row, col, nRows, nCols),
        headerTypes,
      ))
    }
  }
  return cells
}

/**
 * A structural edit, applied in one store transaction so a single Undo reverses it.
 * `shift` is grouped by destination so it maps straight onto `updateMultipleNodes` —
 * one batched commit per moved band, not one per cell.
 */
export interface TableMutation {
  add: TableCellNode[]
  shift: Array<{
    ids: string[]
    /**
     * `x`/`width` ride along because a merge is not only a span change: the surviving cell
     * has to cover the absorbed column on the DRAWING too, or the canvas box stays one
     * column narrow and leaves a hole where the absorbed cell used to be.
     */
    patch: { row?: number; col?: number; col_span?: number; x?: number; width?: number }
  }>
  deleteIds: string[]
}

const EMPTY_MUTATION: TableMutation = { add: [], shift: [], deleteIds: [] }

/**
 * A synthetic box for a cell inserted into an existing table. Never moves a cell that
 * already has one — parsed geometry is a real observation, and re-flowing the table onto a
 * uniform grid would trade it for tidiness. The new cell borrows its cross-axis extent
 * from its neighbour so columns stay aligned, falling back to a slice of the region.
 */
const boxForInsertedCell = (
  model: TableModel,
  row: number,
  col: number,
  axis: 'row' | 'col',
): TableRegion | null => {
  const at = (r: number, c: number): (TableCellNode & Boxed) | undefined => {
    const cell = model.cellByKey.get(cellKey(r, c))
    return cell && hasBox(cell) ? cell : undefined
  }
  if (axis === 'row') {
    const above = at(row - 1, col)
    if (above) {
      return { x: above.x, y: above.y + above.height, width: above.width, height: above.height }
    }
    const below = at(row, col) // still at its pre-shift coordinate
    if (below) {
      return { x: below.x, y: Math.max(0, below.y - below.height), width: below.width, height: below.height }
    }
  } else {
    const left = at(row, col - 1)
    if (left) {
      return { x: left.x + left.width, y: left.y, width: left.width, height: left.height }
    }
    const right = at(row, col)
    if (right) {
      return { x: Math.max(0, right.x - right.width), y: right.y, width: right.width, height: right.height }
    }
  }
  if (!model.region) return null
  const nRows = axis === 'row' ? model.nRows + 1 : model.nRows
  const nCols = axis === 'col' ? model.nCols + 1 : model.nCols
  return interpolateCellBBox(model.region, row, col, nRows, nCols)
}

/** Group the cells whose `row`/`col` moves by one into per-destination batches. */
const shiftBand = (
  model: TableModel,
  axis: 'row' | 'col',
  from: number,
  delta: 1 | -1,
  limit: number,
): TableMutation['shift'] => {
  const byDestination = new Map<number, string[]>()
  for (const cell of model.cellByKey.values()) {
    const index = toIndex(axis === 'row' ? cell.row : cell.col)
    if (index === null || index < from || index >= limit) continue
    const destination = index + delta
    const ids = byDestination.get(destination)
    if (ids) ids.push(cell.id)
    else byDestination.set(destination, [cell.id])
  }
  return Array.from(byDestination.entries()).map(([destination, ids]) => ({
    ids,
    patch: axis === 'row' ? { row: destination } : { col: destination },
  }))
}

/** Insert a blank row at `at`, pushing rows at or below it down by one. */
export function insertRow(
  model: TableModel,
  at: number,
  headerTypes?: HeaderTypes,
): TableMutation {
  if (at < 0 || at > model.nRows) return EMPTY_MUTATION
  const add: TableCellNode[] = []
  for (let col = 0; col < model.nCols; col++) {
    const box = boxForInsertedCell(model, at, col, 'row')
    if (!box) continue // no geometry to anchor to — a box-less cell would be dropped on reload
    add.push(blankCellAt(model.tableIndex, at, col, box, headerTypes))
  }
  return { add, shift: shiftBand(model, 'row', at, 1, model.nRows), deleteIds: [] }
}

/** Insert a blank column at `at`, pushing columns at or right of it over by one. */
export function insertColumn(
  model: TableModel,
  at: number,
  headerTypes?: HeaderTypes,
): TableMutation {
  if (at < 0 || at > model.nCols) return EMPTY_MUTATION
  // A merged cell straddling the insertion point grows, so the header keeps covering the
  // same ground. Emitted as a patch, never by mutating the model's cells.
  const widened = Array.from(model.cellByKey.values())
    .filter((cell) => {
      const c = toIndex(cell.col)
      const span = cell.col_span ?? 1
      return c !== null && span > 1 && c < at && c + span > at
    })
    .map((cell) => ({ ids: [cell.id], patch: { col_span: (cell.col_span ?? 1) + 1 } }))

  const add: TableCellNode[] = []
  for (let row = 0; row < model.nRows; row++) {
    if (coveredBySpan(model, row, at)) continue // a merged cell already covers this slot
    const box = boxForInsertedCell(model, row, at, 'col')
    if (!box) continue
    add.push(blankCellAt(model.tableIndex, row, at, box, headerTypes))
  }
  return {
    add,
    shift: [...shiftBand(model, 'col', at, 1, model.nCols), ...widened],
    deleteIds: [],
  }
}

/** Delete row `at`; rows below shift up. Refuses to empty the table. */
export function deleteRow(model: TableModel, at: number): TableMutation {
  if (at < 0 || at >= model.nRows || model.nRows <= 1) return EMPTY_MUTATION
  const deleteIds: string[] = []
  for (const cell of model.cellByKey.values()) {
    if (toIndex(cell.row) === at) deleteIds.push(cell.id)
  }
  return { add: [], shift: shiftBand(model, 'row', at + 1, -1, model.nRows), deleteIds }
}

/** Delete column `at`; columns to the right shift left. Refuses to empty the table. */
export function deleteColumn(model: TableModel, at: number): TableMutation {
  if (at < 0 || at >= model.nCols || model.nCols <= 1) return EMPTY_MUTATION
  // A merged cell covering the removed column shrinks with it — otherwise it keeps a span
  // wider than the table, rendering a colSpan past the last column and exporting a span that
  // runs off the edge. This includes a merge that STARTS on the removed column: the covered
  // columns hold no node of their own, so deleting the owner would take the header's text and
  // classification with it. It survives, one column narrower, shifted into the freed slot.
  const deleteIds: string[] = []
  const shrunk: TableMutation['shift'] = []
  for (const cell of model.cellByKey.values()) {
    const c = toIndex(cell.col)
    if (c === null) continue
    const span = cell.col_span ?? 1
    const covers = span > 1 && c <= at && c + span > at
    if (c === at && !covers) { deleteIds.push(cell.id); continue }
    if (!covers) continue
    const nextSpan = span - 1
    shrunk.push({
      ids: [cell.id],
      // A merge that started here moves left into the slot the removal frees.
      patch: nextSpan > 1 ? { col_span: nextSpan } : { col_span: 1 },
    })
  }
  return {
    add: [],
    shift: [...shiftBand(model, 'col', at + 1, -1, model.nCols), ...shrunk],
    deleteIds,
  }
}

/** Find a cell by id, with the row/col its KEY gives it — `cell.row`/`cell.col` can be
 *  absent or garbage, and the key is what the grid actually rendered it at. Exported for the
 *  panel, which needs the span to decide whether merge/unmerge apply at all. */
export const findCellById = (
  model: TableModel,
  cellId: string,
): { cell: TableCellNode; row: number; col: number; span: number } | null => {
  for (const [key, cell] of model.cellByKey) {
    if (cell.id !== cellId) continue
    const [row, col] = key.split(':').map(Number)
    if (!Number.isFinite(row) || !Number.isFinite(col)) return null
    return { cell, row, col, span: Math.max(1, cell.col_span ?? 1) }
  }
  return null
}

/**
 * A column's observed x/width, read from any row that has a plain single-column cell there.
 *
 * Merging and unmerging both need it because a column's real extent lives on OTHER rows'
 * cells: the cell being edited spans several columns, so its own width says nothing about
 * where any one of them starts or ends. Merged cells are skipped for exactly that reason.
 */
const columnBox = (model: TableModel, col: number): { x: number; width: number } | null => {
  for (let row = 0; row < model.nRows; row++) {
    const cell = model.cellByKey.get(cellKey(row, col))
    if (cell && (cell.col_span ?? 1) === 1 && hasBox(cell)) return { x: cell.x, width: cell.width }
  }
  return null
}

/**
 * The widths of `span` columns starting at `col`, as recorded by ONE row other than `row`.
 *
 * One row, deliberately: mixing rows is what let unmerge produce overlapping cells. A row
 * only qualifies if it records EVERY column in the span as a plain single-column box, so the
 * proportions describe a real, consistent slicing of that span. `null` when no row does.
 */
const columnWidthsAcross = (
  model: TableModel,
  row: number,
  col: number,
  span: number,
): number[] | null => {
  for (let r = 0; r < model.nRows; r++) {
    if (r === row) continue
    const widths: number[] = []
    for (let c = col; c < col + span; c++) {
      const at = model.cellByKey.get(cellKey(r, c))
      if (!at || (at.col_span ?? 1) !== 1 || !hasBox(at)) break
      widths.push(at.width)
    }
    if (widths.length === span) return widths
  }
  return null
}

/**
 * Merge a SELECTION of adjacent cells into the leftmost one — the "one header over its
 * BASE/ALTERNATE pair" shape a material balance table needs, and the only way to author it.
 *
 * Driven by the canvas multi-selection (Select tool, click each cell), so what you merge is
 * what you picked, rather than a direction the button chose for you.
 *
 * The convention is the one the export and the KG already read (see labelledExport's header
 * note): the surviving cell carries `col_span` and the columns it covers hold NO cell, so a
 * stream name over two case columns is one observation rather than the same text twice.
 *
 * Refuses anything it cannot merge without corrupting the grid, rather than merging part of
 * it: fewer than two cells, cells from more than one row, or a gap between them. Adjacency is
 * measured through spans, so an already-merged cell is absorbed WHOLE — taking half of one
 * would leave the columns it covered with no cell and no owner.
 *
 * Excel's rule on content: the leftmost cell's text wins and the rest is dropped. One Undo
 * brings it back — `applyMutation` snapshots first — so there is no confirm.
 */
export function mergeCells(model: TableModel, ids: readonly string[]): TableMutation {
  const found = ids
    .map((id) => findCellById(model, id))
    .filter((f): f is NonNullable<ReturnType<typeof findCellById>> => f !== null)
  if (found.length < 2) return EMPTY_MUTATION

  const row = found[0].row
  if (found.some((f) => f.row !== row)) return EMPTY_MUTATION

  const sorted = [...found].sort((a, b) => a.col - b.col)
  for (let i = 1; i < sorted.length; i++) {
    // The next cell has to start exactly where the previous one's span ends.
    if (sorted[i].col !== sorted[i - 1].col + sorted[i - 1].span) return EMPTY_MUTATION
  }

  const survivor = sorted[0]
  const absorbed = sorted.slice(1)
  const totalSpan = sorted.reduce((n, f) => n + f.span, 0)

  const patch: TableMutation['shift'][number]['patch'] = { col_span: totalSpan }
  // Cover the absorbed columns on the DRAWING too, or the canvas box stays narrow and leaves
  // a hole where the absorbed cells were. Prefer the last absorbed cell's own right edge;
  // fall back to the column's extent as some other row records it.
  const lastCell = absorbed[absorbed.length - 1].cell
  const lastColumn = columnBox(model, survivor.col + totalSpan - 1)
  const right = hasBox(lastCell)
    ? lastCell.x + lastCell.width
    : lastColumn && lastColumn.x + lastColumn.width
  if (hasBox(survivor.cell) && typeof right === 'number') {
    patch.width = Math.max(MIN_CELL_PX, right - survivor.cell.x)
  }

  return {
    add: [],
    shift: [{ ids: [survivor.cell.id], patch }],
    deleteIds: absorbed.map((f) => f.cell.id),
  }
}

/**
 * Split a merged cell back into one cell per column, refilling the columns it covered.
 *
 * The refilled cells are BLANK: the text the merge dropped is not recoverable from the span,
 * and inventing a copy of the survivor's text in each would fabricate observations the
 * drawing does not show. Undo is the way back to the pre-merge text.
 *
 * `col_span` is set to 1 rather than left off, because the store only upserts a span above 1
 * — a cell patched to `undefined` would keep its stale span on reload (see the clearable-key
 * list in graphSlice's publish path).
 */
export function unmerge(
  model: TableModel,
  cellId: string,
  headerTypes?: HeaderTypes,
): TableMutation {
  const found = findCellById(model, cellId)
  if (!found) return EMPTY_MUTATION
  const { cell, row, col, span } = found
  if (span <= 1) return EMPTY_MUTATION

  // The pieces must TILE the merged cell's own box exactly. Reading each column's absolute
  // x/width straight out of other rows cannot guarantee that: the lookups are per column, so
  // on a table with mixed merges they can land on DIFFERENT reference rows — column 0's width
  // from one row, column 1's x from another — and the refilled cells then overlap or leave a
  // gap. So take the column PROPORTIONS from a single row that records the whole span, and
  // scale them onto the merged box. Falls back to an even split, which tiles by construction.
  const widths = columnWidthsAcross(model, row, col, span) ?? new Array<number>(span).fill(1)
  const add: TableCellNode[] = []
  const patch: TableMutation['shift'][number]['patch'] = { col_span: 1 }

  if (hasBox(cell)) {
    // Cut at cumulative EDGES rather than summing widths, so rounding cannot drift the last
    // piece off the merged box's right edge.
    const total = widths.reduce((sum, w) => sum + w, 0)
    const edges: number[] = [cell.x]
    let acc = 0
    for (const w of widths) {
      acc += w
      edges.push(cell.x + (acc / total) * cell.width)
    }
    patch.width = Math.max(MIN_CELL_PX, edges[1] - edges[0])
    for (let i = 1; i < span; i++) {
      add.push(
        blankCellAt(
          model.tableIndex,
          row,
          col + i,
          {
            x: edges[i],
            y: cell.y,
            width: Math.max(MIN_CELL_PX, edges[i + 1] - edges[i]),
            height: cell.height,
          },
          headerTypes,
        ),
      )
    }
  }

  return { add, shift: [{ ids: [cell.id], patch }], deleteIds: [] }
}

/**
 * Unmerge every merged cell in a selection, in one transaction.
 *
 * Cells that are not merged are skipped rather than refusing the whole action. This is
 * defensive, not a UX affordance: the panel only offers Unmerge when EVERY selected cell is
 * merged, so a mixed set should not reach here — and if one does, splitting what can be split
 * beats doing nothing silently.
 */
export function unmergeCells(
  model: TableModel,
  ids: readonly string[],
  headerTypes?: HeaderTypes,
): TableMutation {
  const parts = ids.map((id) => unmerge(model, id, headerTypes)).filter((m) => m.shift.length > 0)
  if (parts.length === 0) return EMPTY_MUTATION
  return {
    add: parts.flatMap((m) => m.add),
    shift: parts.flatMap((m) => m.shift),
    deleteIds: [],
  }
}

/** Remove the whole table. `deleteRow`/`deleteColumn` refuse to empty it, so without this
 *  there is no way to get rid of one. */
export function deleteTable(model: TableModel): TableMutation {
  return { add: [], shift: [], deleteIds: Array.from(model.cellByKey.values(), (c) => c.id) }
}

/**
 * Re-apply `headerTypes`, returning only the cells that actually change. Shrinking the
 * band clears rows that are no longer header — otherwise 2 → 1 leaves row 1 typed while
 * the panel shows it as body, and the stale class reaches the export.
 */
export function reclassifyHeaders(model: TableModel, headerTypes: HeaderTypes): TableCellNode[] {
  const changed: TableCellNode[] = []
  for (const cell of model.cellByKey.values()) {
    const row = toIndex(cell.row)
    const col = toIndex(cell.col)
    if (row === null || col === null) continue
    const next = classificationForCell(row, col, headerTypes)
    if (next) {
      if (cell.pnid_level_i === next.pnid_level_i
      && cell.pnid_level_ii === next.pnid_level_ii
      && cell.pnid_level_iii === next.pnid_level_iii) continue
      changed.push({ ...cell, ...next })
      continue
    }
    // Not a header cell — clear any class it still carries. '' not undefined, so the
    // store records a real clear (its upsert keys off level_i being set).
    if (!cell.pnid_level_i && !cell.pnid_level_ii && !cell.pnid_level_iii) continue
    changed.push({ ...cell, pnid_level_i: '', pnid_level_ii: '', pnid_level_iii: '' })
  }
  return changed
}

/**
 * A geometry-only patch for one cell. Each cell gets its OWN values, so this is applied per
 * node by the caller — the store's `updateMultipleNodes` takes a single shared patch for many
 * ids and does not fit.
 */
export interface CellGeometryPatch {
  id: string
  x?: number
  y?: number
  width?: number
  height?: number
}

/** The eight box-resize handles. Anything else moves no wall — see {@link wallsFor}. */
const BOX_HANDLES = new Set(['tl', 'tr', 'bl', 'br', 't', 'r', 'b', 'l'])

/**
 * Which walls a resize handle moved. `tl`/`tr`/`bl`/`br` move two.
 *
 * Unknown handles move nothing. The membership test is not paranoia: the substring checks
 * below would read `vertex:0` (a polygon-vertex drag, ENG-2582) as a TOP-wall drag, on the
 * `t` in "vertex". PnidRenderer filters those out before it calls, so this is a guard on the
 * exported contract rather than a live bug.
 */
function wallsFor(handle: string): { left: boolean; right: boolean; top: boolean; bottom: boolean } {
  if (!BOX_HANDLES.has(handle)) return { left: false, right: false, top: false, bottom: false }
  return {
    left: handle.includes('l'),
    right: handle.includes('r'),
    top: handle.includes('t'),
    bottom: handle.includes('b'),
  }
}

/**
 * Floor for a coupled cell's width/height, matching PnidRenderer's own MIN_SIZE so a dragged
 * wall and a coupled one clamp at the same place. Clamping the WALL rather than each cell is
 * what keeps the grid contiguous: clamp per-cell and the crushed column overlaps its
 * neighbour, which still shifted the full distance.
 */
const MIN_CELL_PX = 10

/** Cells whose box is fully known — the only ones we can safely move. */
function boxOf(cell: TableCellLike): TableRegion | null {
  const { x, y, width, height } = cell
  if (x === undefined || y === undefined || width === undefined || height === undefined) return null
  return { x, y, width, height }
}

/**
 * Where the dragged boundary currently sits in EACH row (for a column drag) or each column
 * (for a row drag), keyed by that line.
 *
 * A ragged grid has a different answer per line, and that is the whole reason this exists:
 * the cells that OWN the boundary get snapped to an absolute wall, so if the cells past it
 * were shifted by one global delta instead, a line whose boundary started somewhere else
 * would land off the wall — a gap or an overlap, exactly the raggedness the snap is meant to
 * remove. Each line therefore moves by its own (wall - oldWall).
 *
 * Measured from `farSide` (the cells beyond the boundary — the boundary as the group being
 * shifted sees it, so a pre-existing split at the boundary closes), falling back to
 * `nearSide` (the owning cells' far edge) for a line that has nothing beyond it.
 */
function boundaryPerLine(
  farSide: Array<{ line: number; edge: number }>,
  nearSide: Array<{ line: number; edge: number }>,
): Map<number, number> {
  const byLine = new Map<number, number>()
  for (const { line, edge } of farSide) {
    const seen = byLine.get(line)
    if (seen === undefined || edge < seen) byLine.set(line, edge)
  }
  for (const { line, edge } of nearSide) if (!byLine.has(line)) byLine.set(line, edge)
  return byLine
}

/**
 * Move a whole column/row boundary when one cell's wall is dragged — Excel's model.
 *
 * Each cell is an independent rectangle (see {@link blankCellAt} — every cell carries its
 * own `x/y/width/height`), so a bare resize moves one cell's wall and leaves the column
 * ragged: that row's boundary sits somewhere the other rows' does not.
 *
 * Excel semantics: dragging a column's right border resizes THAT column in every row and
 * SHIFTS every column to its right, widths unchanged, so the table gets wider. The
 * neighbouring column is not squeezed. (In Excel a single cell cannot even have its own
 * width — width belongs to the column — so the ragged state this fixes is not
 * representable there at all.)
 *
 * `table` MUST be the PRE-DRAG grid and `before`/`after` the dragged cell's own rect on
 * either side of the gesture. That split is deliberate, and is the whole reason this
 * signature takes rects instead of deriving a delta from the grid:
 *
 *   - the neighbours have to be measured from where they sat BEFORE the drag, and
 *   - the caller runs on pointer-up, one statement after the dragged cell was committed
 *     to the store, so the grid it can see is the pre-drag one anyway.
 *
 * An earlier version read the delta out of the grid instead. Because the caller's grid is
 * pre-drag, the dragged cell still measured at its old size, every delta came out 0, and
 * the "snap the column straight" rule below dutifully straightened the column onto the
 * OLD boundary — a no-op at best, and a visible jump backwards on a ragged column. Passing
 * the two rects is what makes the gesture observable at all; do not "simplify" it away.
 *
 * A column's cells are snapped to the new boundary rather than each shifted by the delta,
 * so a drag also STRAIGHTENS a column that was already ragged. Left/top drags additionally
 * restore the dragged cell's own width/height: the renderer pins the opposite edge and
 * resizes it (PnidRenderer's `case 'l'`), where Excel keeps the width and moves the cell.
 *
 * Returns one patch per cell to move — the dragged cell included when its own geometry has
 * to be corrected. Cells with incomplete geometry are skipped, and no patch is ever
 * non-positive.
 */
export function coupleWallResize<C extends TableCellLike>(
  table: TableModel<C>,
  resizedId: string,
  handle: string,
  before: TableRegion,
  after: TableRegion,
): CellGeometryPatch[] {
  const cells: Array<{ cell: C; box: TableRegion; r: number; c: number; span: number }> = []
  let target: { r: number; c: number; span: number } | null = null
  for (const [key, cell] of table.cellByKey) {
    const box = boxOf(cell)
    if (!box) continue
    const [r, c] = key.split(':').map(Number)
    const span = Math.max(1, cell.col_span ?? 1)
    cells.push({ cell, box, r, c, span })
    if (cell.id === resizedId) target = { r, c, span }
  }
  if (!target) return []

  const walls = wallsFor(handle)
  const patches = new Map<string, CellGeometryPatch>()
  const put = (id: string, patch: Omit<CellGeometryPatch, 'id'>) => {
    patches.set(id, { ...(patches.get(id) ?? { id }), ...patch })
  }
  const lastCol = target.c + target.span - 1

  if (walls.right) {
    // The band whose right edge IS this boundary. The dragged cell is in it, and is
    // included rather than skipped: if the clamp below moves the wall, its own committed
    // width is wrong too and has to be corrected.
    const band = cells.filter((e) => e.c + e.span - 1 === lastCol)
    const wall = Math.max(after.x + after.width, ...band.map((e) => e.box.x + MIN_CELL_PX))
    const oldWall = boundaryPerLine(
      cells.filter((e) => e.c > lastCol).map((e) => ({ line: e.r, edge: e.box.x })),
      band.map((e) => ({ line: e.r, edge: e.box.x + e.box.width })),
    )
    const fallback = before.x + before.width
    const delta = wall - fallback
    for (const e of cells) {
      // Snap to the wall. Absolute, not a delta, so a column that was already ragged comes
      // out straight.
      if (e.c + e.span - 1 === lastCol) put(e.cell.id, { width: wall - e.box.x })
      // A merged cell CONTAINS the boundary, so snapping would swallow its later columns —
      // it grows by the delta instead, keeping its far edge relative. Its row has no
      // boundary of its own to measure, so this is the one case that uses the global delta.
      else if (e.c <= lastCol && e.c + e.span - 1 > lastCol) {
        put(e.cell.id, { width: Math.max(MIN_CELL_PX, e.box.width + delta) })
      }
      // Everything past it shifts, width unchanged — by ITS OWN row's move, not the dragged
      // row's, or a row that was ragged keeps its offset and lands off the wall.
      else if (e.c > lastCol) {
        put(e.cell.id, { x: e.box.x + (wall - (oldWall.get(e.r) ?? fallback)) })
      }
    }
  }

  if (walls.left) {
    // Excel's boundary always resizes the column to its LEFT, so a left drag is really a
    // drag of the previous column's right edge. Column 0 has no previous column: there is
    // nothing to donate the space, so the table's outer edge moves and THIS column changes
    // width instead — the renderer's own behaviour, applied to the whole column. Without
    // this case the whole table slides sideways, since no cell matches column -1.
    if (target.c === 0) {
      const col = cells.filter((e) => e.c === 0)
      const wall = Math.min(after.x, ...col.map((e) => e.box.x + e.box.width - MIN_CELL_PX))
      for (const e of col) put(e.cell.id, { x: wall, width: e.box.x + e.box.width - wall })
    } else {
      const prev = cells.filter((e) => e.c + e.span - 1 === target.c - 1)
      const wall = Math.max(after.x, ...prev.map((e) => e.box.x + MIN_CELL_PX))
      const oldWall = boundaryPerLine(
        cells.filter((e) => e.c >= target.c).map((e) => ({ line: e.r, edge: e.box.x })),
        prev.map((e) => ({ line: e.r, edge: e.box.x + e.box.width })),
      )
      const fallback = before.x
      const delta = wall - fallback
      for (const e of cells) {
        // The column before the dragged one owns this wall: snap its right edge to it.
        if (e.c + e.span - 1 === target.c - 1) put(e.cell.id, { width: wall - e.box.x })
        // A merged cell spanning the wall keeps its left edge and follows on the right.
        else if (e.c < target.c && e.c + e.span - 1 >= target.c) {
          put(e.cell.id, { width: Math.max(MIN_CELL_PX, e.box.width + delta) })
        }
        // The dragged column and everything after shift. The renderer pinned the dragged
        // cell's right edge and grew it; Excel moves the cell instead, so its width goes
        // back to what it was.
        else if (e.c >= target.c) {
          put(
            e.cell.id,
            e.cell.id === resizedId
              ? { x: wall, width: before.width }
              : { x: e.box.x + (wall - (oldWall.get(e.r) ?? fallback)) },
          )
        }
      }
    }
  }

  // Rows are the same two shapes, minus merging — this model has no row_span.
  if (walls.bottom) {
    const band = cells.filter((e) => e.r === target.r)
    const wall = Math.max(after.y + after.height, ...band.map((e) => e.box.y + MIN_CELL_PX))
    const oldWall = boundaryPerLine(
      cells.filter((e) => e.r > target.r).map((e) => ({ line: e.c, edge: e.box.y })),
      band.map((e) => ({ line: e.c, edge: e.box.y + e.box.height })),
    )
    const fallback = before.y + before.height
    for (const e of cells) {
      if (e.r === target.r) put(e.cell.id, { height: wall - e.box.y })
      else if (e.r > target.r) {
        put(e.cell.id, { y: e.box.y + (wall - (oldWall.get(e.c) ?? fallback)) })
      }
    }
  }

  if (walls.top) {
    if (target.r === 0) {
      const row = cells.filter((e) => e.r === 0)
      const wall = Math.min(after.y, ...row.map((e) => e.box.y + e.box.height - MIN_CELL_PX))
      for (const e of row) put(e.cell.id, { y: wall, height: e.box.y + e.box.height - wall })
    } else {
      const prev = cells.filter((e) => e.r === target.r - 1)
      const wall = Math.max(after.y, ...prev.map((e) => e.box.y + MIN_CELL_PX))
      const oldWall = boundaryPerLine(
        cells.filter((e) => e.r >= target.r).map((e) => ({ line: e.c, edge: e.box.y })),
        prev.map((e) => ({ line: e.c, edge: e.box.y + e.box.height })),
      )
      const fallback = before.y
      for (const e of cells) {
        if (e.r === target.r - 1) put(e.cell.id, { height: wall - e.box.y })
        else if (e.r >= target.r) {
          put(
            e.cell.id,
            e.cell.id === resizedId
              ? { y: wall, height: before.height }
              : { y: e.box.y + (wall - (oldWall.get(e.c) ?? fallback)) },
          )
        }
      }
    }
  }

  return Array.from(patches.values())
}
